<template>
  <div class="dashboard-container">
    <div class="dashboard-header">
      <div>
        <h1>儀錶板</h1>
        <div class="date-display">
          <i class="far fa-calendar-alt"></i>
          {{ currentDate }}
        </div>
      </div>

      <button class="refresh-button" :disabled="isLoading" @click="loadDashboard">
        <i class="fas fa-sync-alt" :class="{ 'fa-spin': isLoading }"></i>
        {{ isLoading ? "讀取中" : "重新整理" }}
      </button>
    </div>

    <div v-if="errorMessage" class="dashboard-error">
      <i class="fas fa-exclamation-circle"></i>
      <span>{{ errorMessage }}</span>
    </div>

    <!-- 今日卡片全部來自 /report/dashboard，不再顯示寫死數字。 -->
    <div class="stats-container">
      <div v-for="card in statsCards" :key="card.key" class="stat-card">
        <div class="stat-icon">
          <i :class="card.icon"></i>
        </div>
        <div class="stat-info">
          <div class="stat-value app-num">
            {{ isLoading ? "—" : card.value }}
          </div>
          <div class="stat-label">{{ card.label }}</div>
        </div>
        <div
          v-if="!isLoading"
          class="stat-trend"
          :class="isTrendPositive(card) ? 'positive' : 'negative'"
          :title="`相較昨日 ${card.trend >= 0 ? '+' : ''}${card.trend}%`"
        >
          <i
            class="fas"
            :class="card.trend >= 0 ? 'fa-arrow-up' : 'fa-arrow-down'"
          ></i>
          <span>{{ Math.abs(card.trend) }}%</span>
        </div>
      </div>
    </div>

    <div class="charts-container">
      <div class="chart-card">
        <div class="chart-header">
          <div>
            <h3>已完成訂單營業額趨勢</h3>
            <small>取消單不列入營業額</small>
          </div>
          <div class="chart-actions">
            <select v-model="salesPeriod" :disabled="isLoading">
              <option value="week">近 7 天</option>
              <option value="month">近 30 天</option>
              <option value="year">本年度</option>
            </select>
          </div>
        </div>
        <div class="chart-content">
          <div v-if="isLoading" class="empty-state">
            <i class="fas fa-spinner fa-spin"></i>
            正在讀取營業額...
          </div>
          <div v-else-if="normalizedSalesBars.length === 0" class="empty-state">
            此期間沒有已完成訂單
          </div>
          <div v-else class="sales-chart">
            <div
              v-for="item in normalizedSalesBars"
              :key="item.date"
              class="sales-bar-column"
              :title="`${item.date}｜${formatCurrency(item.revenue)}｜${item.orders} 筆`"
            >
              <div class="sales-bar-value">
                {{ item.revenue > 0 ? formatNumber(item.revenue) : "" }}
              </div>
              <div class="bar-track">
                <div class="bar" :style="{ height: `${item.height}%` }"></div>
              </div>
              <div class="sales-bar-label">{{ formatBarLabel(item) }}</div>
            </div>
          </div>
        </div>
      </div>

      <div class="chart-card">
        <div class="chart-header">
          <div>
            <h3>熱門商品</h3>
            <small>依已完成訂單銷售份數排序</small>
          </div>
        </div>
        <div class="chart-content hot-product-content">
          <div v-if="isLoading" class="empty-state">
            <i class="fas fa-spinner fa-spin"></i>
            正在讀取熱門商品...
          </div>
          <div v-else-if="hotProducts.length === 0" class="empty-state">
            此期間沒有商品銷售資料
          </div>
          <div v-else class="hot-product-list">
            <div
              v-for="product in hotProducts"
              :key="product.menuItemId"
              class="hot-product-row"
            >
              <div class="hot-product-info">
                <div>
                  <strong>{{ product.name }}</strong>
                  <small>{{ product.categoryName }}</small>
                </div>
                <div class="hot-product-number">
                  {{ formatNumber(product.totalQuantity) }} 份 ·
                  {{ formatCurrency(product.totalSales) }}
                </div>
              </div>
              <div class="hot-product-track">
                <div
                  class="hot-product-bar"
                  :style="{ width: hotProductWidth(product.totalQuantity) }"
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="tables-container">
      <div class="table-card">
        <div class="table-header">
          <div>
            <h3>最新訂單</h3>
            <small>顯示所選統計期間內最新 5 筆</small>
          </div>
        </div>
        <div class="table-content">
          <div v-if="isLoading" class="empty-state table-empty">
            正在讀取訂單...
          </div>
          <div v-else-if="recentOrders.length === 0" class="empty-state table-empty">
            此期間沒有訂單
          </div>
          <table v-else>
            <thead>
              <tr>
                <th>訂單編號</th>
                <th>客戶</th>
                <th>金額</th>
                <th>狀態</th>
                <th>時間</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="order in recentOrders" :key="order.id">
                <td>{{ order.orderNumber }}</td>
                <td>{{ order.customerName }}</td>
                <td>{{ formatCurrency(order.totalAmount) }}</td>
                <td>
                  <span class="status-tag" :class="statusClass(order.status)">
                    {{ order.status }}
                  </span>
                </td>
                <td>{{ formatDateTime(order.orderDate) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="table-card">
        <div class="table-header">
          <div>
            <h3>付款方式</h3>
            <small>只統計已完成訂單</small>
          </div>
        </div>
        <div class="payment-content">
          <div v-if="isLoading" class="empty-state">
            正在讀取付款方式...
          </div>
          <div v-else-if="paymentMethods.length === 0" class="empty-state">
            此期間沒有已完成付款資料
          </div>
          <template v-else>
            <div class="payment-pie" :style="paymentPieStyle">
              <div class="payment-pie-hole">
                <strong>{{ formatCurrency(paymentMethods.reduce((sum, item) => sum + item.amount, 0)) }}</strong>
                <small>完成營業額</small>
              </div>
            </div>
            <div class="payment-list">
              <div
                v-for="item in paymentMethods"
                :key="item.paymentMethod"
                class="payment-row"
              >
                <span>{{ item.paymentMethod }}</span>
                <strong>{{ formatCurrency(item.amount) }}</strong>
                <em>{{ item.percentage.toFixed(1) }}%</em>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script src="@/assets/Js/systemset/systemdashboard.js"></script>
<style scoped src="@/assets/Css/systemset/systemdashboard.css"></style>
